// LeadsStore — MongoDB-backed leads via /api/leads.
// - In-memory cache populated on mount + polled every 15s for fresh server state.
// - Optimistic updates: Wingman / UI mutations patch the cache instantly, then PATCH the server.
// - Subscribers (React useSyncExternalStore) re-render on any cache change.

import { useSyncExternalStore } from 'react';
import axios from 'axios';
import { getStoredUser } from './authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LEADS_API = `${BACKEND_URL}/api/leads`;
// Poll cadence — 60s is plenty for a CRM where the underlying source of
// truth is human-driven (LO actions every few minutes). At the 8k+ lead
// scale, a 15s cadence + full-payload re-download made the UI noticeably
// laggy. Single-lead detail views fetch fresh on open, so this never
// stales the page the LO is actively staring at.
const POLL_MS = 60000;

let cache = [];                  // current array of leads from server
let cacheVersion = 0;            // bump on any change so useSyncExternalStore re-runs
let _loaded = false;             // becomes true after first successful fetch
let _lastSig = '';               // skip-emit signature to avoid pointless re-renders
const subs = new Set();
let _pendingFilter = null;       // queued by Wingman before NAV /leads
let _hydrated = false;
let _pollTimer = null;

// ── On-demand single-lead cache ──────────────────────────────────────────
// The list-cache (`cache`) only holds the top-500 highest-scored leads to
// keep the polling payload manageable at 100k+ production scale. ANY lead
// outside that window — searched-for, deep-linked, freshly mailer-imported,
// etc. — must be fetched individually via GET /api/leads/{id} or the Lead
// Profile screen renders "Lead not found" (the original prod bug). This
// secondary map survives the 60s `fetchAll` poll, so a lead you're actively
// viewing doesn't disappear after the next refresh.
const _byId = {};                // id -> full lead doc
const _byIdLoading = new Set();  // id -> in-flight fetch
const _byIdNotFound = new Set(); // id -> backend returned 404

function emit() {
  cacheVersion += 1;
  subs.forEach((fn) => { try { fn(); } catch {} });
}

function snapshot() { return cacheVersion; }

function subscribe(fn) {
  subs.add(fn);
  ensureHydrated();
  return () => subs.delete(fn);
}

async function fetchAll() {
  // ── Progressive-emission strategy ─────────────────────────────────────
  // At 100k+ lead scale the admin bootstrap fires FOUR parallel fetches
  // (top-500 by score, pipeline, my-leads, all-claimed). The all-claimed
  // fetch is capped at 10k and can take 8-12s on a cold Mongo cache while
  // the my-leads fetch typically finishes in <1s. Previously we `await
  // Promise.all(...)` so the UI stared at a spinner for the slowest one
  // even though "My Leads" is fully renderable from the tiny me-scoped
  // response.
  //
  // New behaviour:
  //   • Every fetch runs in parallel, but we MERGE + EMIT after each one
  //     resolves individually.
  //   • The PRIMARY fetch (top-500 by score for admin, or top-500 scoped
  //     to the LO's assignedTo) is treated as the "loaded" milestone —
  //     `_loaded` flips true and the page hides its loading state the
  //     moment this returns.
  //   • Pipeline / all-claimed populate the cache in the background;
  //     Pipeline / Reports pages update seamlessly when they arrive.
  //   • Individual failures don't kill the whole load — a slow all-claimed
  //     query can't hide the user's own leads from them.
  const me = getStoredUser();
  const baseParams = { light: true };
  if (me?.id && me?.role !== 'admin') {
    baseParams.assigned_to = me.id;
  }
  const merged = new Map();
  cache.forEach((l) => { if (l?.id) merged.set(l.id, l); });

  const mergeAndEmit = (rows, { markLoaded = false } = {}) => {
    if (Array.isArray(rows)) {
      for (const l of rows) {
        if (l?.id) merged.set(l.id, l);
      }
    }
    const next = Array.from(merged.values());
    const sig = next.length + ':' + next.reduce(
      (s, l) => s + (l.id?.charCodeAt(0) || 0) + (l.updatedAt?.charCodeAt(0) || 0),
      0,
    );
    if (sig === _lastSig && _loaded) return;
    _lastSig = sig;
    cache = next;
    if (markLoaded) _loaded = true;
    emit();
  };

  const guard = (p, label) => p.catch((e) => {
    console.warn(`LeadsStore.${label} failed`, e?.message || e);
    return { data: [] };
  });

  // (A) PRIMARY — top-500 by score (role-scoped for LOs). This is the
  //     fastest fetch and it alone lets the Leads page render its list.
  const primary = guard(
    axios.get(LEADS_API, { params: { ...baseParams, limit: 500 } }),
    'primary',
  );
  // (B) Pipeline — every lead currently in the funnel.
  const pipeline = guard(
    axios.get(LEADS_API, { params: { ...baseParams, limit: 5000, loan_status_set: true } }),
    'pipeline',
  );
  // (C) Me — every lead assigned to the signed-in user.
  const mine = me?.id
    ? guard(
        axios.get(LEADS_API, { params: { light: true, limit: 5000, assigned_to: me.id } }),
        'mine',
      )
    : null;
  // (D) All claimed — admin-only, keeps Reports Claims aligned with
  //     Mailer Dashboard's count. Slowest at scale — pushed to the tail.
  const allClaimed = me?.role === 'admin'
    ? guard(
        axios.get(LEADS_API, { params: { light: true, limit: 10000, assigned_to_set: true } }),
        'all-claimed',
      )
    : null;

  // ── Emit progressively ──────────────────────────────────────────────
  // Kick off the "me" fetch first as an EMIT trigger — for an LO it fully
  // populates the My Leads view, and for admins it seeds their own row
  // set before the wider primary lands.
  if (mine) {
    mine.then((r) => mergeAndEmit(r?.data, { markLoaded: true }));
  }
  primary.then((r) => mergeAndEmit(r?.data, { markLoaded: true }));
  pipeline.then((r) => mergeAndEmit(r?.data));
  if (allClaimed) {
    allClaimed.then((r) => mergeAndEmit(r?.data));
  }

  // Return a promise that resolves when EVERY fetch has settled — used
  // by the 60s poll and by callers that explicitly await LeadsStore.refresh().
  await Promise.allSettled([primary, pipeline, mine, allClaimed].filter(Boolean));
  return cache;
}

function ensureHydrated() {
  if (_hydrated) return;
  _hydrated = true;
  fetchAll();
  // Skip polls while the tab is hidden or the previous poll is still running,
  // so idle/background tabs don't pile heavy list queries onto the server.
  let inflight = false;
  _pollTimer = setInterval(() => {
    if (inflight || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
    inflight = true;
    fetchAll().finally(() => { inflight = false; });
  }, POLL_MS);
  // Re-fetch immediately when auth state changes (login / logout) so the
  // role-scoped `assigned_to` param flips without a 60s poll delay.
  if (typeof window !== 'undefined') {
    window.addEventListener('kf:auth-change', () => {
      // Drop the dedupe sig + by-id cache so the next fetch always emits.
      _lastSig = '';
      _loaded = false;
      for (const k of Object.keys(_byId)) delete _byId[k];
      _byIdLoading.clear();
      _byIdNotFound.clear();
      fetchAll();
    });
  }
}

// --- Selectors ---
function getMergedLeads() { return cache; }
function getMergedLead(id) {
  // Prefer the on-demand single-lead cache (`_byId`) — that's the FULL doc
  // populated by GET /leads/{id} — over the list-cache, which only holds
  // a `light=true` projection (no `application.currentAddress`, no
  // `application.dob`, etc.). Without this preference, opening a profile
  // from My Leads would render the form using the stripped projection
  // and the LO would see empty fields for the borrower's residence address,
  // SSN, employment, etc. — even though the data persists fine in Mongo.
  return _byId[id] || cache.find((l) => l.id === id) || null;
}

async function fetchLeadById(id) {
  // On-demand fetch for the FULL lead doc — including the heavy
  // `application` blob (currentAddress, SSN, employment, income, assets,
  // etc.) that the `light=true` list-cache projection deliberately strips.
  //
  // We deliberately DO NOT short-circuit on a list-cache hit any more.
  // Earlier versions did, but the consequence was that opening a profile
  // from My Leads (where the lead IS in the list-cache, just stripped)
  // never populated `_byId` → the Lead Profile's `useFullLead` gate
  // stayed Loading forever. Always do the round-trip; the only valid
  // short-circuit is when `_byId[id]` is already populated.
  if (!id) return null;
  if (_byId[id]) return _byId[id];
  if (_byIdLoading.has(id)) return null;
  _byIdLoading.add(id);
  emit();
  try {
    const r = await axios.get(`${LEADS_API}/${id}`);
    _byId[id] = r.data;
    _byIdNotFound.delete(id);
    emit();
    return r.data;
  } catch (e) {
    // 404 → mark not-found so LeadProfile can render the empty state
    // instead of an infinite spinner. 403 (LO trying to view an unclaimed
    // lead) is functionally indistinguishable to this UI — bounce them
    // back the same way; the row-level claim CTA in SearchResults is the
    // canonical path for unclaimed leads anyway.
    const status = e?.response?.status;
    if (status === 404 || status === 403) _byIdNotFound.add(id);
    emit();
    return null;
  } finally {
    _byIdLoading.delete(id);
    emit();
  }
}

function getLeadFetchState(id) {
  return {
    loading: _byIdLoading.has(id),
    notFound: _byIdNotFound.has(id),
  };
}

function findLeadIdByName(name) {
  if (!name) return null;
  const q = name.toLowerCase().trim();
  const exact = cache.find((l) => l.name.toLowerCase() === q);
  if (exact) return exact.id;
  const partial = cache.find((l) => l.name.toLowerCase().includes(q));
  return partial ? partial.id : null;
}

// --- Mutations ---
function coerceValue(field, raw) {
  if (raw == null) return raw;
  const numFields = ['score', 'estAmount'];
  if (numFields.includes(field)) {
    const n = Number(String(raw).replace(/[$,]/g, ''));
    return isNaN(n) ? raw : n;
  }
  if (typeof raw === 'string') return raw.replace(/^["'`]|["'`]$/g, '').trim();
  return raw;
}

async function updateLead(id, patch) {
  if (!id || !patch) return null;
  const cleaned = {};
  for (const [k, v] of Object.entries(patch)) cleaned[k] = coerceValue(k, v);

  // Locate the lead in whichever cache(s) hold it. There are three real
  // states at production scale (100k+ leads):
  //
  //   (A) list-cache hit  — lead is in the top-500 (`cache` array).
  //   (B) byId-cache hit  — lead was opened on the Lead Profile screen
  //                         on-demand (sits in `_byId`).
  //   (C) NEITHER         — lead was rendered by SearchResults (which
  //                         calls /api/leads?q=… directly and doesn't
  //                         touch either cache). The "Claim lead" CTA
  //                         lives on this row, so we MUST handle this
  //                         path or the claim silently fails with
  //                         "Could not claim this lead".
  //
  // Strategy: do an optimistic write in (A) and (B); in (C) skip the
  // optimistic step (we have no `before` to roll back to anyway) and
  // just PATCH the server. On success, inject the returned doc into
  // `_byId` so the next render finds it and the toast/click-through
  // pipeline works.
  const idx = cache.findIndex((l) => l.id === id);
  const inList = idx !== -1;
  const inByid = !!_byId[id];
  const before = inList ? cache[idx] : (inByid ? _byId[id] : null);

  if (before) {
    const optimistic = { ...before, ...cleaned };
    if (inList) cache = [...cache.slice(0, idx), optimistic, ...cache.slice(idx + 1)];
    if (inByid) _byId[id] = optimistic;
    emit();
  }

  // Persist to server
  try {
    const r = await axios.patch(`${LEADS_API}/${id}`, cleaned);
    const idx2 = cache.findIndex((l) => l.id === id);
    if (idx2 !== -1) cache = [...cache.slice(0, idx2), r.data, ...cache.slice(idx2 + 1)];
    // Always seed `_byId` with the freshly-returned doc — keeps subsequent
    // optimistic edits on off-cache leads (the SearchResults claim flow)
    // working without an extra round-trip.
    _byId[id] = r.data;
    _byIdNotFound.delete(id);
    emit();
    return r.data;
  } catch (e) {
    // Roll back on failure (only meaningful if we did an optimistic write).
    if (before) {
      const idx2 = cache.findIndex((l) => l.id === id);
      if (idx2 !== -1) cache = [...cache.slice(0, idx2), before, ...cache.slice(idx2 + 1)];
      if (inByid) _byId[id] = before;
      emit();
    }
    console.error('LeadsStore.updateLead failed', e);
    return null;
  }
}

async function createLead(payload) {
  try {
    const r = await axios.post(LEADS_API, payload);
    cache = [r.data, ...cache];
    emit();
    return r.data;
  } catch (e) {
    console.error('LeadsStore.createLead failed', e);
    return null;
  }
}

async function deleteLead(id) {
  // Optimistic remove
  const idx = cache.findIndex((l) => l.id === id);
  if (idx === -1) return false;
  const before = cache[idx];
  cache = cache.filter((l) => l.id !== id);
  emit();
  try {
    await axios.delete(`${LEADS_API}/${id}`);
    return true;
  } catch (e) {
    // Roll back
    cache = [...cache.slice(0, idx), before, ...cache.slice(idx)];
    emit();
    return false;
  }
}

function setPendingFilter(v) { _pendingFilter = v || null; }
function consumePendingFilter() { const v = _pendingFilter; _pendingFilter = null; return v; }

// --- React hooks ---
export function useLeads() {
  useSyncExternalStore(subscribe, snapshot);
  return getMergedLeads();
}

export function useLead(id) {
  useSyncExternalStore(subscribe, snapshot);
  return getMergedLead(id);
}

export function useFullLead(id) {
  // Strict accessor that returns ONLY the full GET /leads/{id} payload,
  // never the light-projected version. Used by the Lead Profile form to
  // gate hydration — the borrower's `currentAddress`, SSN, employment,
  // income, etc. live in fields the light projection strips out, so the
  // form must wait for the full doc before initializing `data` or it'll
  // render with empty fields and then re-clobber the user's first edits
  // when the full payload eventually arrives.
  useSyncExternalStore(subscribe, snapshot);
  return _byId[id] || null;
}

export function useLeadFetchState(id) {
  useSyncExternalStore(subscribe, snapshot);
  return getLeadFetchState(id);
}

export function useLeadsLoaded() {
  useSyncExternalStore(subscribe, snapshot);
  return _loaded;
}

// --- Imperative API used by the Wingman executor ---
export const LeadsStore = {
  getAll: getMergedLeads,
  get: getMergedLead,
  fetchById: fetchLeadById,
  update: updateLead,
  patchLead: updateLead,
  create: createLead,
  remove: deleteLead,
  findIdByName: findLeadIdByName,
  setPendingFilter,
  consumePendingFilter,
  refresh: fetchAll,
};
